import React, { useState, useEffect } from "react";
import { message } from "antd";
import {
  DevicePhoneMobileIcon,
  BanknotesIcon,
  CreditCardIcon,
  DocumentTextIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";
import { processPayment, PaymentPayload } from "../apis/payment";
import Modal from "../../src/components/Modal";

type PaymentMode = "upi" | "cash" | "card" | "cheque" | "online";

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  total: number;
  discount?: number;
  amountAlreadyPaid?: number;
  patientName?: string;
  patientId?: number;
  sourceType: "visit" | "pathology" | "pharmacy";
  sourceId?: number;
  allowPartialOrUnpaid?: boolean;
  testNames?: string[];
  selectedOrderIds?: number[];
  orderAmounts?: Record<number, number>;
  advanceBalance?: number;
  cashlessInsuranceAmount?: number;
  onSave?: (paymentData: PaymentData) => void;
  onPrint?: () => void;
  onPaymentSuccess?: () => void;
}

export interface PaymentData {
  upi: number;
  cash: number;
  card: number;
  cheque: number;
  online: number;
  paymentNote: string;
  transactionRef?: string;
  advanceUsed?: number;
  insuranceUsed?: number;
  roundOff?: number; // signed: positive = rounded up, negative = rounded down
}

type ModeAmounts = Record<PaymentMode, number>;

const emptyModes = (): ModeAmounts => ({
  upi: 0,
  cash: 0,
  card: 0,
  cheque: 0,
  online: 0,
});

const MODES: { key: PaymentMode; label: string; Icon: React.ComponentType<any> }[] = [
  { key: "cash", label: "Cash", Icon: BanknotesIcon },
  { key: "upi", label: "UPI", Icon: DevicePhoneMobileIcon },
  { key: "card", label: "Card", Icon: CreditCardIcon },
  { key: "cheque", label: "Cheque", Icon: DocumentTextIcon },
  { key: "online", label: "Online", Icon: GlobeAltIcon },
];

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  total,
  discount = 0,
  amountAlreadyPaid = 0,
  sourceType,
  sourceId,
  allowPartialOrUnpaid = false,
  testNames = [],
  patientName,
  selectedOrderIds = [],
  orderAmounts = {},
  advanceBalance,
  cashlessInsuranceAmount,
  onSave,
  onPaymentSuccess,
}) => {
  const [modeAmounts, setModeAmounts] = useState<ModeAmounts>(emptyModes());
  const [transactionRef, setTransactionRef] = useState("");
  const [paymentNote, setPaymentNote] = useState("");
  const [advanceUsed, setAdvanceUsed] = useState(0);
  const [insuranceUsed, setInsuranceUsed] = useState(0);

  const safeTotal = Number(total) || 0;
  const safeDiscount = Number(discount) || 0;
  const safeAlreadyPaid = Number(amountAlreadyPaid) || 0;
  const grossAmount = safeTotal - safeDiscount;
  const fullAmountDue = Math.round(grossAmount);
  const calculatedRoundOff = +(fullAmountDue - grossAmount).toFixed(2);
  const amountDue = Math.max(fullAmountDue - safeAlreadyPaid, 0);

  const modeTotal = MODES.reduce((sum, m) => sum + (Number(modeAmounts[m.key]) || 0), 0);
  const effectivePaying = modeTotal + advanceUsed + insuranceUsed;
  const balance = effectivePaying - amountDue;
  const remaining = Math.max(amountDue - advanceUsed - insuranceUsed - modeTotal, 0);
  const isFullyPaid = balance >= 0;
  const canSave =
    (allowPartialOrUnpaid || balance >= 0) &&
    (modeTotal > 0 || advanceUsed > 0 || insuranceUsed > 0);
  const hasNonCash =
    modeAmounts.upi > 0 ||
    modeAmounts.card > 0 ||
    modeAmounts.cheque > 0 ||
    modeAmounts.online > 0;

  useEffect(() => {
    if (isOpen) {
      setTransactionRef("");
      setPaymentNote("");
      setAdvanceUsed(0);
      setInsuranceUsed(0);
      // Default: put full due amount on cash; user can split across modes
      setModeAmounts({ ...emptyModes(), cash: amountDue });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const setModeAmount = (mode: PaymentMode, value: string) => {
    const raw = parseFloat(value);
    const requested = Number.isFinite(raw) ? Math.max(raw, 0) : 0;
    const maxPayable = Math.max(amountDue - advanceUsed - insuranceUsed, 0);

    setModeAmounts((prev) => {
      if (mode === "cash") {
        const otherModes = MODES.reduce(
          (sum, m) => sum + (m.key === "cash" ? 0 : Number(prev[m.key]) || 0),
          0
        );
        const cappedCash = Math.min(requested, Math.max(maxPayable - otherModes, 0));
        return { ...prev, cash: cappedCash };
      }

      // Non-cash: cap so total never exceeds due; reduce cash automatically if needed
      const otherNonCash = MODES.reduce(
        (sum, m) =>
          sum +
          (m.key === "cash" || m.key === mode ? 0 : Number(prev[m.key]) || 0),
        0
      );
      const maxForMode = Math.max(maxPayable - otherNonCash, 0);
      const capped = Math.min(requested, maxForMode);
      const next: ModeAmounts = { ...prev, [mode]: capped };
      const nonCashTotal = MODES.reduce(
        (sum, m) => sum + (m.key === "cash" ? 0 : Number(next[m.key]) || 0),
        0
      );
      const maxCash = Math.max(maxPayable - nonCashTotal, 0);
      next.cash = Math.min(Number(prev.cash) || 0, maxCash);
      return next;
    });
  };

  const fillRemainingOnMode = (mode: PaymentMode) => {
    const otherModes = MODES.reduce(
      (sum, m) => sum + (m.key === mode ? 0 : Number(modeAmounts[m.key]) || 0),
      0
    );
    const fill = Math.max(amountDue - advanceUsed - insuranceUsed - otherModes, 0);
    setModeAmounts((prev) => {
      const next = { ...prev, [mode]: fill };
      if (mode !== "cash") {
        // Filling a non-cash mode should consume from cash first
        const nonCashTotal = MODES.reduce(
          (sum, m) => sum + (m.key === "cash" ? 0 : Number(next[m.key]) || 0),
          0
        );
        const maxPayable = Math.max(amountDue - advanceUsed - insuranceUsed, 0);
        next.cash = Math.min(Number(prev.cash) || 0, Math.max(maxPayable - nonCashTotal, 0));
      }
      return next;
    });
  };

  const clampModesToPayable = (maxPayable: number, prev: ModeAmounts): ModeAmounts => {
    const next = { ...prev };
    const nonCashTotal = MODES.reduce(
      (sum, m) => sum + (m.key === "cash" ? 0 : Number(next[m.key]) || 0),
      0
    );
    if (nonCashTotal > maxPayable) {
      // Scale down non-cash proportionally if needed (edge case)
      let remainingBudget = maxPayable;
      for (const m of MODES) {
        if (m.key === "cash") continue;
        const amt = Number(next[m.key]) || 0;
        const capped = Math.min(amt, remainingBudget);
        next[m.key] = capped;
        remainingBudget = Math.max(remainingBudget - capped, 0);
      }
      next.cash = 0;
      return next;
    }
    next.cash = Math.min(Number(next.cash) || 0, Math.max(maxPayable - nonCashTotal, 0));
    return next;
  };

  const handleAdvanceChange = (value: string) => {
    const raw = parseFloat(value) || 0;
    const next = Math.min(Math.max(raw, 0), advanceBalance || 0);
    setAdvanceUsed(next);
    const maxPayable = Math.max(amountDue - next - insuranceUsed, 0);
    setModeAmounts((prev) => clampModesToPayable(maxPayable, prev));
  };

  const handleInsuranceChange = (value: string) => {
    const raw = parseFloat(value) || 0;
    const maxInsurance = Math.min(cashlessInsuranceAmount || 0, amountDue - advanceUsed);
    const next = Math.min(Math.max(raw, 0), maxInsurance);
    setInsuranceUsed(next);
    const maxPayable = Math.max(amountDue - advanceUsed - next, 0);
    setModeAmounts((prev) => clampModesToPayable(maxPayable, prev));
  };

  const buildPaymentData = (): PaymentData => ({
    upi: modeAmounts.upi || 0,
    cash: modeAmounts.cash || 0,
    card: modeAmounts.card || 0,
    cheque: modeAmounts.cheque || 0,
    online: modeAmounts.online || 0,
    paymentNote,
    transactionRef: hasNonCash ? transactionRef : undefined,
    advanceUsed,
    insuranceUsed,
    roundOff: calculatedRoundOff,
  });

  const handleSave = async () => {
    if (effectivePaying > amountDue + 0.001) {
      message.error("Paying amount cannot be greater than amount due");
      return;
    }
    if (!canSave) {
      if (modeTotal <= 0 && advanceUsed <= 0 && insuranceUsed <= 0) {
        message.error("Enter at least one payment amount");
      } else {
        message.error("Paying amount should not be less than amount due");
      }
      return;
    }

    if (onSave) {
      onSave(buildPaymentData());
      return;
    }

    try {
      const currentDate = new Date().toISOString().split("T")[0];
      const paymentModes: Array<{
        amount: number;
        payment_mode: string;
        payment_date: string;
        transaction_id?: string;
        notes?: string;
        allocation_type: "manual";
      }> = [];

      MODES.forEach(({ key }) => {
        const amount = Number(modeAmounts[key]) || 0;
        if (amount <= 0) return;
        paymentModes.push({
          amount,
          payment_mode: key,
          payment_date: currentDate,
          transaction_id:
            key !== "cash" && transactionRef ? transactionRef : undefined,
          notes: paymentNote || undefined,
          allocation_type: "manual",
        });
      });

      if (advanceUsed > 0) {
        paymentModes.push({
          amount: advanceUsed,
          payment_mode: "cash",
          payment_date: currentDate,
          notes: "Advance allocation",
          allocation_type: "manual",
        });
      }
      if (insuranceUsed > 0) {
        paymentModes.push({
          amount: insuranceUsed,
          payment_mode: "insurance",
          payment_date: currentDate,
          notes: "Insurance cashless payment",
          allocation_type: "manual",
        });
      }

      const sourceIds =
        selectedOrderIds && selectedOrderIds.length > 0
          ? selectedOrderIds
          : sourceId
          ? [sourceId]
          : [];

      if (sourceIds.length === 0 && sourceType !== "pathology") {
        message.error("No orders selected for payment");
        return;
      }

      const mappedSourceType = sourceType === "pathology" ? "lab_order" : sourceType;

      const payload: PaymentPayload = {
        source_type: mappedSourceType as "lab_order" | "pharmacy_sale" | "visit",
        source_ids: sourceIds,
        payments: paymentModes,
        invoice_date: currentDate,
      };

      if (selectedOrderIds && selectedOrderIds.length > 1) {
        payload.source_amounts = {};
        selectedOrderIds.forEach((orderId) => {
          payload.source_amounts![orderId] = orderAmounts[orderId] || 0;
        });
      } else if (
        selectedOrderIds &&
        selectedOrderIds.length === 1 &&
        orderAmounts[selectedOrderIds[0]]
      ) {
        payload.source_amounts = {
          [selectedOrderIds[0]]: orderAmounts[selectedOrderIds[0]],
        };
      }

      await processPayment(payload);
      message.success("Payment processed successfully");
      onPaymentSuccess?.();
      onClose();
    } catch (err: any) {
      message.error(err?.message || "Payment failed. Please try again!");
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      title="Payment Details"
      width="sm:max-w-xl"
      onClose={onClose}
      modalContent={
        <div className="space-y-4">
          {/* Patient + Amount Due */}
          <div className="flex items-start justify-between">
            <div>
              {patientName && (
                <div className="font-bold text-gray-900 text-base">{patientName}</div>
              )}
              {testNames.length > 0 && (
                <div className="text-sm text-gray-500 mt-0.5">
                  {testNames.join(", ")}
                </div>
              )}
            </div>
            <div className="text-right flex-shrink-0 ml-4">
              <div className="text-xs uppercase tracking-wider text-gray-400 font-medium">
                Amount Due
              </div>
              <div className="text-2xl font-bold text-gray-900">
                ₹{amountDue.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Summary Card */}
          <div
            className={`bg-gray-50 rounded-xl p-4 grid gap-3 ${
              safeAlreadyPaid > 0 ? "grid-cols-4" : "grid-cols-3"
            }`}
          >
            <div>
              <div className="text-xs text-gray-500 mb-1">Total</div>
              <div className="font-semibold text-gray-900">₹{safeTotal.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Discount</div>
              <div className="font-semibold text-red-500">– ₹{safeDiscount.toFixed(2)}</div>
            </div>
            {safeAlreadyPaid > 0 && (
              <div>
                <div className="text-xs text-gray-500 mb-1">Paid</div>
                <div className="font-semibold text-green-600">– ₹{safeAlreadyPaid.toFixed(2)}</div>
              </div>
            )}
            <div>
              <div className="text-xs text-gray-500 mb-1">Round Off</div>
              <div className="font-semibold text-gray-700">
                {calculatedRoundOff >= 0 ? "+" : ""}₹{calculatedRoundOff.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Advance */}
          {typeof advanceBalance === "number" && advanceBalance > 0 && (
            <div className="flex items-center gap-3 bg-green-50 rounded-xl p-3">
              <div className="flex-1">
                <div className="text-xs text-gray-500">Advance Available</div>
                <div className="font-semibold text-green-700">
                  ₹{Number(advanceBalance).toFixed(2)}
                </div>
              </div>
              <div className="flex-1">
                <div className="text-xs text-gray-500 mb-1">Use Advance</div>
                <input
                  type="number"
                  min={0}
                  max={advanceBalance}
                  value={advanceUsed || ""}
                  onChange={(e) => handleAdvanceChange(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
                  placeholder="0"
                />
              </div>
            </div>
          )}

          {/* Insurance */}
          {cashlessInsuranceAmount != null && cashlessInsuranceAmount > 0 && (
            <div className="flex items-center gap-3 bg-blue-50 rounded-xl p-3">
              <div className="flex-1">
                <div className="text-xs text-gray-500">Insurance Approved</div>
                <div className="font-semibold text-blue-700">
                  ₹{Number(cashlessInsuranceAmount).toFixed(2)}
                </div>
              </div>
              <div className="flex-1">
                <div className="text-xs text-gray-500 mb-1">Apply Insurance</div>
                <input
                  type="number"
                  min={0}
                  max={Math.min(cashlessInsuranceAmount, amountDue - advanceUsed)}
                  value={insuranceUsed || ""}
                  onChange={(e) => handleInsuranceChange(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
                  placeholder="0"
                />
              </div>
            </div>
          )}

          {/* Split payment modes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-gray-400 font-medium">
                Payment Modes
              </div>
              <div className="text-xs text-gray-500">
                Remaining{" "}
                <span
                  className={`font-semibold ${
                    remaining === 0 ? "text-green-600" : "text-orange-600"
                  }`}
                >
                  ₹{remaining.toFixed(2)}
                </span>
              </div>
            </div>
            <p className="text-xs text-gray-400 mb-2">
              Split across modes — e.g. Cash ₹500 + UPI ₹500
            </p>
            <div className="space-y-2">
              {MODES.map(({ key, label, Icon }) => {
                const amount = Number(modeAmounts[key]) || 0;
                const active = amount > 0;
                return (
                  <div
                    key={key}
                    className={`flex items-center gap-3 rounded-xl border-2 px-3 py-2 transition-colors ${
                      active
                        ? "border-primary bg-indigo-50/60"
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    <div
                      className={`flex items-center justify-center w-9 h-9 rounded-lg ${
                        active ? "bg-indigo-100 text-primary" : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="w-16 text-sm font-medium text-gray-800">{label}</div>
                    <div className="flex-1 flex items-center border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus-within:border-primary">
                      <span className="text-gray-400 text-sm mr-1">₹</span>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        max={amountDue}
                        value={amount || ""}
                        onChange={(e) => setModeAmount(key, e.target.value)}
                        className="w-full outline-none text-sm font-semibold text-gray-900 bg-transparent"
                        placeholder="0"
                      />
                    </div>
                    {remaining > 0 && (
                      <button
                        type="button"
                        onClick={() => fillRemainingOnMode(key)}
                        className="text-xs font-medium text-primary hover:underline whitespace-nowrap"
                        title={`Put remaining ₹${remaining.toFixed(2)} on ${label}`}
                      >
                        Fill rest
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Transaction Reference (when any non-cash mode used) */}
          {hasNonCash && (
            <div>
              <div className="text-xs text-gray-500 mb-1">Transaction Reference</div>
              <input
                type="text"
                value={transactionRef}
                onChange={(e) => setTransactionRef(e.target.value)}
                placeholder="UPI / card / cheque / online reference"
                className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors"
              />
            </div>
          )}

          {/* Paying / Balance Summary */}
          <div
            className={`rounded-xl p-4 ${
              isFullyPaid ? "bg-green-50" : "bg-orange-50"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                  Paying
                </div>
                <div className="text-lg font-bold text-gray-900">
                  ₹{effectivePaying.toFixed(2)}
                </div>
                {modeTotal > 0 && (
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {MODES.filter((m) => modeAmounts[m.key] > 0)
                      .map((m) => `${m.label} ₹${modeAmounts[m.key].toFixed(0)}`)
                      .join(" + ")}
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-500 font-medium">
                  Balance
                </div>
                <div
                  className={`text-lg font-bold ${
                    isFullyPaid ? "text-green-600" : "text-orange-600"
                  }`}
                >
                  ₹{Math.abs(balance).toFixed(2)}
                </div>
              </div>
              <div
                className={`text-sm font-semibold px-4 py-1.5 rounded-full ${
                  isFullyPaid
                    ? "bg-green-100 text-green-700"
                    : "bg-orange-100 text-orange-600"
                }`}
              >
                {isFullyPaid ? "Fully paid" : "Pending"}
              </div>
            </div>
          </div>

          {/* Payment Note */}
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-400 font-medium mb-2">
              Payment Note
            </div>
            <textarea
              rows={2}
              value={paymentNote}
              onChange={(e) => setPaymentNote(e.target.value)}
              placeholder="Enter any additional payment notes..."
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm resize-none focus:outline-none focus:border-primary transition-colors"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              className="px-6 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave}
              className="px-6 py-2 text-sm font-semibold text-white bg-primary rounded-full hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              Save Payment
            </button>
          </div>
        </div>
      }
    />
  );
};

export default PaymentModal;
